/**
 * Colour vision deficiency simulation for whole images (2026-10-15, the Color Blindness Simulator). Each pixel is
 * linearised, multiplied by the Machado, Oliveira and Fernandes (2009) matrix of the condition (severity 1.0 for the
 * -opias, 0.6 for the -omalies) and re-encoded to sRGB; the two monochromacies use the relative luminance (fully for
 * achromatopsia, blended 60% for achromatomaly). Lookup tables keep a 1600 pixel image under a few tens of ms.
 */

export type CvdType =
  'protanomaly' | 'protanopia' | 'deuteranomaly' | 'deuteranopia' |
  'tritanomaly' | 'tritanopia' | 'achromatomaly' | 'achromatopsia';

export interface CvdInfo {
  type: CvdType;
  name: string;
  group: string;
  note: string;
  /** how common it is, for the caption */
  share: string;
}

export const CVD_TYPES: CvdInfo[] = [
  { type: 'deuteranomaly', name: 'Deuteranomaly', group: 'Red And Green', note: 'Green looks redder and duller', share: 'About 5% of men' },
  { type: 'deuteranopia', name: 'Deuteranopia', group: 'Red And Green', note: 'Red and green are confused', share: 'About 1% of men' },
  { type: 'protanomaly', name: 'Protanomaly', group: 'Red And Green', note: 'Red looks greener and darker', share: 'About 1% of men' },
  { type: 'protanopia', name: 'Protanopia', group: 'Red And Green', note: 'Red looks dark, red and green are confused', share: 'About 1% of men' },
  { type: 'tritanomaly', name: 'Tritanomaly', group: 'Blue And Yellow', note: 'Blue and green converge', share: 'Rare' },
  { type: 'tritanopia', name: 'Tritanopia', group: 'Blue And Yellow', note: 'Blue and yellow are confused', share: 'Rare' },
  { type: 'achromatomaly', name: 'Achromatomaly', group: 'No Color', note: 'Colors heavily faded', share: 'Very rare' },
  { type: 'achromatopsia', name: 'Achromatopsia', group: 'No Color', note: 'Only lightness, a grayscale world', share: 'Very rare' },
];

type M3 = [ number, number, number, number, number, number, number, number, number ];

const MATRICES: Partial<Record<CvdType, M3>> = {
  protanopia: [ 0.152286, 1.052583, -0.204868, 0.114503, 0.786281, 0.099216, -0.003882, -0.048116, 1.051998 ],
  deuteranopia: [ 0.367322, 0.860646, -0.227968, 0.280085, 0.672501, 0.047413, -0.011820, 0.042940, 0.968881 ],
  tritanopia: [ 1.255528, -0.076749, -0.178779, -0.078411, 0.930809, 0.147602, 0.004733, 0.691367, 0.303900 ],
  protanomaly: [ 0.385450, 0.769005, -0.154455, 0.100526, 0.829802, 0.069673, -0.007442, -0.022190, 1.029632 ],
  deuteranomaly: [ 0.547494, 0.607765, -0.155259, 0.181692, 0.781742, 0.036566, -0.010410, 0.027275, 0.983136 ],
  tritanomaly: [ 1.104996, -0.046633, -0.058363, -0.032137, 0.971635, 0.060503, 0.001336, 0.317922, 0.680742 ],
};

// sRGB byte -> linear, and linear (0..1 in 4096 steps) -> sRGB byte
const TO_LINEAR = new Float32Array( 256 );
for ( let i = 0; i < 256; i++ ) {
  const c = i / 255;
  TO_LINEAR[ i ] = c <= 0.04045 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 );
}
const STEPS = 4096;
const TO_SRGB = new Uint8ClampedArray( STEPS + 1 );
for ( let i = 0; i <= STEPS; i++ ) {
  const c = i / STEPS;
  TO_SRGB[ i ] = Math.round( 255 * ( c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow( c, 1 / 2.4 ) - 0.055 ) );
}
const enc = ( v: number ) => TO_SRGB[ v <= 0 ? 0 : v >= 1 ? STEPS : Math.round( v * STEPS ) ];

/** a new ImageData: `source` as the condition sees it (alpha kept) */
export function simulateCvd( source: ImageData, type: CvdType ): ImageData {
  const src = source.data;
  const out = new ImageData( source.width, source.height );
  const dst = out.data;
  const m = MATRICES[ type ];
  const mono = type === 'achromatopsia' ? 1 : type === 'achromatomaly' ? 0.6 : 0;
  for ( let i = 0; i < src.length; i += 4 ) {
    const r = TO_LINEAR[ src[ i ] ], g = TO_LINEAR[ src[ i + 1 ] ], b = TO_LINEAR[ src[ i + 2 ] ];
    let nr: number, ng: number, nb: number;
    if ( m ) {
      nr = m[ 0 ] * r + m[ 1 ] * g + m[ 2 ] * b;
      ng = m[ 3 ] * r + m[ 4 ] * g + m[ 5 ] * b;
      nb = m[ 6 ] * r + m[ 7 ] * g + m[ 8 ] * b;
    } else {
      const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      nr = r + ( y - r ) * mono;
      ng = g + ( y - g ) * mono;
      nb = b + ( y - b ) * mono;
    }
    dst[ i ] = enc( nr );
    dst[ i + 1 ] = enc( ng );
    dst[ i + 2 ] = enc( nb );
    dst[ i + 3 ] = src[ i + 3 ];
  }
  return out;
}

/** a random Codigrate theme's JetBrains screenshot from the catalogue, the simulator's opening picture */
export function randomThemeScreenshot( products: { name: string; platforms?: { name?: string; info?: { name?: string }; screenshot?: { src?: string } }[] }[] ): { src: string; theme: string } | null {
  // only the single themes (cities, nature) have a start picture; All In One stays out
  const shots = products
    .map( p => ( { theme: p.name, src: ( p.platforms || [] ).find( pl => /jetbrains/i.test( ( pl.info?.name || pl.name || '' ) ) && pl.screenshot?.src )?.screenshot?.src || '' } ) )
    .map( s => ( { theme: s.theme, dir: ( s.src.match( /\/(?:cities|nature)\/([a-z-]+-theme)\// ) || [] )[ 1 ] } ) )
    .filter( s => s.dir );
  if ( !shots.length ) {
    return null;
  }
  // The start picture: the screenshot laid over the theme's own sharp wallpaper (generate-cvd-samples.js).
  const shot = shots[ Math.floor( Math.random() * shots.length ) ];
  return { theme: shot.theme, src: './assets/tools/color-blindness/' + shot.dir + '.webp' };
}
